import { motion } from 'framer-motion';
import { cn } from '@/utils/classnames';

/**
 * Static SVG diagram showing the identity flow:
 *   Feishu Identity → Cbwee Identity Fabric (OIDC / Access / Audit) → Downstream apps
 *
 * Connection lines animate via stroke-dashoffset; each node pulses with a
 * gentle box-shadow keyframe.
 */
export interface IdentityFlowProps {
  className?: string;
}

const NODE_BOX_BASE =
  'relative z-10 flex flex-col items-center justify-center rounded-lg border border-outline-variant px-stack-md py-stack-sm text-center bg-surface';

export function IdentityFlow({ className }: IdentityFlowProps) {
  return (
    <div className={cn('flex w-full flex-col items-center gap-stack-md md:flex-row md:items-stretch', className)}>
      <FlowNode label="Feishu Identity" subtitle="飞书身份源" highlight={false} />
      <FlowConnector />
      <FlowNode label="Cbwee Identity Fabric" subtitle="OIDC · Access · Audit" highlight />
      <FlowConnector />
      <FlowNode label="Downstream Apps" subtitle="业务应用" highlight={false} />
    </div>
  );
}

function FlowNode({ label, subtitle, highlight }: { label: string; subtitle: string; highlight: boolean }) {
  return (
    <motion.div
      animate={
        highlight
          ? { boxShadow: ['0 0 0 0 rgba(0,82,204,0)', '0 0 0 6px rgba(0,82,204,0.18)', '0 0 0 0 rgba(0,82,204,0)'] }
          : { boxShadow: '0 0 0 0 rgba(0,0,0,0)' }
      }
      transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
      className={cn(
        NODE_BOX_BASE,
        'flex-1',
        highlight ? 'border-primary bg-primary-container text-on-primary' : '',
      )}
    >
      <span className="font-headline-md text-headline-md">{label}</span>
      <span
        className={cn(
          'mt-unit font-label-caps text-label-caps uppercase',
          highlight ? 'text-on-primary/80' : 'text-on-surface-variant',
        )}
      >
        {subtitle}
      </span>
    </motion.div>
  );
}

function FlowConnector() {
  return (
    <div className="flex w-full items-center justify-center md:w-12">
      <svg
        viewBox="0 0 80 12"
        preserveAspectRatio="none"
        className="h-3 w-full md:h-12 md:w-[80px]"
        aria-hidden
      >
        <line
          x1="0"
          y1="6"
          x2="80"
          y2="6"
          stroke="#737685"
          strokeWidth="1.5"
          strokeDasharray="4 4"
        >
          <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="1.6s" repeatCount="indefinite" />
        </line>
      </svg>
    </div>
  );
}
